import { useCallback } from 'react';

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

interface Position {
	x: number;
	y: number;
}

interface UseSnakeMovementProps {
	snake: Position[];
	directionRef: React.MutableRefObject<Direction>;
	nextDirectionRef: React.MutableRefObject<Direction>;
}

interface UseSnakeMovementReturn {
	/**
	 * Calculate next head position based on current direction
	 */
	calculateNextHead: () => Position;

	/**
	 * Move snake forward without growing (remove tail, add head)
	 */
	moveSnakeForward: () => Position[];

	/**
	 * Grow snake by adding new head and keeping all segments
	 */
	growSnake: (newHead: Position) => Position[];

	/**
	 * Shrink snake by removing segments from tail
	 */
	shrinkSnake: (segments: number) => Position[];

	/**
	 * Update direction with validation (prevents 180-degree turns)
	 */
	changeDirection: (newDirection: Direction) => boolean;

	/**
	 * Get current direction
	 */
	getCurrentDirection: () => Direction;

	/**
	 * Check if a direction change is valid
	 */
	isValidDirectionChange: (newDirection: Direction) => boolean;
}

/**
 * Custom hook to manage snake movement logic for Letter Party game
 *
 * Handles:
 * - Direction calculation and validation
 * - Snake position updates (move, grow, shrink)
 * - Prevention of 180-degree turns
 *
 * @param snake - Current snake positions
 * @param directionRef - Current direction ref
 * @param nextDirectionRef - Next direction ref
 * @returns Movement control functions
 */
export function useSnakeMovement({
	snake,
	directionRef,
	nextDirectionRef,
}: UseSnakeMovementProps): UseSnakeMovementReturn {
	/**
	 * Calculate the next head position based on current direction
	 */
	const calculateNextHead = useCallback((): Position => {
		const direction = nextDirectionRef.current;
		const currentHead = snake[0];

		switch (direction) {
			case 'UP':
				return { x: currentHead.x, y: currentHead.y - 1 };
			case 'DOWN':
				return { x: currentHead.x, y: currentHead.y + 1 };
			case 'LEFT':
				return { x: currentHead.x - 1, y: currentHead.y };
			case 'RIGHT':
				return { x: currentHead.x + 1, y: currentHead.y };
		}
	}, [snake, nextDirectionRef]);

	/**
	 * Move snake forward by one cell (remove tail, add new head)
	 */
	const moveSnakeForward = useCallback((): Position[] => {
		const newHead = calculateNextHead();
		return [newHead, ...snake.slice(0, -1)];
	}, [snake, calculateNextHead]);

	/**
	 * Grow snake by adding new head while keeping all segments
	 */
	const growSnake = useCallback(
		(newHead: Position): Position[] => {
			return [newHead, ...snake];
		},
		[snake],
	);

	/**
	 * Shrink snake by removing segments from the tail
	 * Ensures at least 1 segment remains (the head)
	 */
	const shrinkSnake = useCallback(
		(segments: number): Position[] => {
			const newLength = Math.max(1, snake.length - segments);
			return snake.slice(0, newLength);
		},
		[snake],
	);

	/**
	 * Check if a direction change is valid (no 180-degree turns)
	 */
	const isValidDirectionChange = useCallback(
		(newDirection: Direction): boolean => {
			const currentDirection = directionRef.current;

			// Map of opposite directions
			const opposites: Record<Direction, Direction> = {
				UP: 'DOWN',
				DOWN: 'UP',
				LEFT: 'RIGHT',
				RIGHT: 'LEFT',
			};

			// Can't turn 180 degrees
			return opposites[currentDirection] !== newDirection;
		},
		[directionRef],
	);

	/**
	 * Change direction with validation
	 * Returns true if direction was changed, false if invalid
	 */
	const changeDirection = useCallback(
		(newDirection: Direction): boolean => {
			if (!isValidDirectionChange(newDirection)) {
				return false;
			}

			nextDirectionRef.current = newDirection;
			return true;
		},
		[nextDirectionRef, isValidDirectionChange],
	);

	/**
	 * Get the current direction
	 */
	const getCurrentDirection = useCallback((): Direction => {
		return directionRef.current;
	}, [directionRef]);

	return {
		calculateNextHead,
		moveSnakeForward,
		growSnake,
		shrinkSnake,
		changeDirection,
		getCurrentDirection,
		isValidDirectionChange,
	};
}
